import { api } from '../../lib/apiClient';

export type ConnectorTool = {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
};

export type ConnectorDefinition = {
  slug: string;
  name: string;
  vendor: string;
  description: string;
  icon: string;
  docs_url?: string;
  scopes: string[];
  tools: ConnectorTool[];
  status: 'alpha' | 'beta' | 'ga';
  credential_type: 'oauth' | 'api_key';
};

export type OAuthClient = {
  id: string;
  vendor: string;
  client_id: string;
  created_at: string;
  updated_at: string;
};

export type Connection = {
  id: string;
  definition_slug: string;
  account_label: string;
  scopes_granted: string[];
  status: 'connected' | 'expired' | 'revoked';
  expires_at?: string;
  created_at: string;
  last_used_at?: string;
};

export async function listCatalog(orgId: string): Promise<ConnectorDefinition[]> {
  const res = await api.get<{ data: { items: ConnectorDefinition[] } }>(
    `/v1/orgs/${orgId}/connectors`,
  );
  return res.data.data?.items ?? [];
}

export async function startConnect(
  orgId: string,
  definition: string,
  returnTo: string,
): Promise<{ authorize_url: string }> {
  const res = await api.post<{ data: { authorize_url: string } }>(
    `/v1/orgs/${orgId}/connectors/oauth/start`,
    { definition, return_to: returnTo },
  );
  return res.data.data;
}

export async function listOAuthClients(orgId: string): Promise<OAuthClient[]> {
  const res = await api.get<{ data: { items: OAuthClient[] } }>(
    `/v1/orgs/${orgId}/oauth-clients`,
  );
  return res.data.data?.items ?? [];
}

export async function upsertOAuthClient(
  orgId: string,
  input: { vendor: string; client_id: string; client_secret: string },
): Promise<void> {
  await api.post(`/v1/orgs/${orgId}/oauth-clients`, input);
}

export async function deleteOAuthClient(orgId: string, vendor: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/oauth-clients/${vendor}`);
}

export async function listConnections(orgId: string): Promise<Connection[]> {
  const res = await api.get<{ data: { items: Connection[] } }>(
    `/v1/orgs/${orgId}/connections`,
  );
  return res.data.data?.items ?? [];
}

export async function deleteConnection(orgId: string, connectionId: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/connections/${connectionId}`);
}

export async function createAPIKeyConnection(
  orgId: string,
  input: {
    definition: string;
    label: string;
    api_key: string;
    api_secret: string;
    totp_secret?: string;
  },
): Promise<Connection> {
  const res = await api.post<{ data: Connection }>(
    `/v1/orgs/${orgId}/connections/api-key`,
    input,
  );
  return res.data.data;
}

// updateConnectionTOTP sets (or clears, with "") the TOTP seed on an
// existing api_key connection. Useful when the operator first set up
// the connection without 2FA and now wants to add it.
export async function updateConnectionTOTP(
  orgId: string,
  connectionId: string,
  totpSecret: string,
): Promise<void> {
  await api.post(`/v1/orgs/${orgId}/connections/${connectionId}/totp`, {
    totp_secret: totpSecret,
  });
}

// generateConnectionTOTPCode returns the current 6-digit code derived
// from the stored seed plus when the current 30s step ends, so the
// portal can render a countdown next to the code.
export async function generateConnectionTOTPCode(
  orgId: string,
  connectionId: string,
): Promise<{ code: string; expires_at: string }> {
  const res = await api.post<{ data: { code: string; expires_at: string } }>(
    `/v1/orgs/${orgId}/connections/${connectionId}/totp/code`,
    {},
  );
  return res.data.data;
}

export async function listAgentConnections(
  orgId: string,
  agentId: string,
): Promise<Connection[]> {
  const res = await api.get<{ data: { items: Connection[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/connections`,
  );
  return res.data.data?.items ?? [];
}

export async function setAgentConnections(
  orgId: string,
  agentId: string,
  connectionIds: string[],
): Promise<void> {
  await api.post(`/v1/orgs/${orgId}/agents/${agentId}/connections`, {
    connection_ids: connectionIds,
  });
}
